"use client";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import type React from "react";
import { cn } from "@/lib/utils";

/**
 * Sheet — a side drawer built on Base UI's Dialog. Used to host the shell's
 * sidebar on mobile (where the fixed 256px rail is hidden). `side` chooses the
 * edge it slides from; the enter/exit slide uses Base UI's
 * data-starting-style / data-ending-style hooks.
 */

export const Sheet: typeof DialogPrimitive.Root = DialogPrimitive.Root;
export const SheetPortal: typeof DialogPrimitive.Portal =
  DialogPrimitive.Portal;

export function SheetTrigger(
  props: DialogPrimitive.Trigger.Props,
): React.ReactElement {
  return <DialogPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

export function SheetClose(
  props: DialogPrimitive.Close.Props,
): React.ReactElement {
  return <DialogPrimitive.Close data-slot="sheet-close" {...props} />;
}

export function SheetBackdrop({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props): React.ReactElement {
  return (
    <DialogPrimitive.Backdrop
      className={cn(
        "fixed inset-0 z-50 bg-black/32 backdrop-blur-sm transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0",
        className,
      )}
      data-slot="sheet-backdrop"
      {...props}
    />
  );
}

const SIDE_CLASS = {
  left: "inset-y-0 left-0 border-r data-ending-style:-translate-x-full data-starting-style:-translate-x-full",
  right:
    "inset-y-0 right-0 border-l data-ending-style:translate-x-full data-starting-style:translate-x-full",
} as const;

export function SheetContent({
  className,
  children,
  side = "left",
  width = 288,
  portalProps,
  ...props
}: DialogPrimitive.Popup.Props & {
  side?: keyof typeof SIDE_CLASS;
  width?: number;
  portalProps?: DialogPrimitive.Portal.Props;
}): React.ReactElement {
  return (
    <SheetPortal {...portalProps}>
      <SheetBackdrop />
      <DialogPrimitive.Popup
        className={cn(
          "fixed z-50 flex h-full max-w-[85vw] flex-col overflow-y-auto bg-[var(--nav-bg)] text-[var(--nav-ink)] shadow-lg/5 transition-transform duration-200 ease-out will-change-transform",
          SIDE_CLASS[side],
          className,
        )}
        data-slot="sheet-content"
        style={{ width }}
        {...props}
      >
        {children}
      </DialogPrimitive.Popup>
    </SheetPortal>
  );
}

export function SheetTitle({
  className,
  ...props
}: DialogPrimitive.Title.Props): React.ReactElement {
  return (
    <DialogPrimitive.Title
      className={cn("font-heading font-semibold text-lg leading-none", className)}
      data-slot="sheet-title"
      {...props}
    />
  );
}

export function SheetDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props): React.ReactElement {
  return (
    <DialogPrimitive.Description
      className={cn("text-muted-foreground text-sm", className)}
      data-slot="sheet-description"
      {...props}
    />
  );
}

export { DialogPrimitive as SheetPrimitive };
